import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createPatientEnrollment,
  createPendingPatientEnrollment,
  deletePatientEnrollment,
  deletePatientEnrollmentItems,
  deletePendingPatientEnrollment,
  getPatientById,
  getPatientEnrollmentById,
  getPatientEnrollments,
  getPendingPatientEnrollmentById,
  getPendingPatientEnrollments,
  updatePendingPatientEnrollment,
} from "../dataAccess/apiService";
import { useBoundStore } from "../stores/useBoundStore";
import {
  BulkDeleteItemsRequest,
  CreatePatientEnrollmentRequest,
  PatientEnrollment,
  PendingPatientEnrollment,
} from "@clients/velys-infrastructure/api";
import { useEffect, useState } from "react";
import { fetchJsonData } from "../helpers";

export interface PendingEnrollmentPage {
  data: PendingPatientEnrollment[];
}

export interface EnrollmentPage {
  data: PatientEnrollment[];
}

// get pending enrollments
export const useGetPendingEnrollments = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);
  const orgSurgeons = useBoundStore((state) => state.orgSurgeons);
  const isSurgeonsInitialized = useBoundStore(
    (state) => state.isSurgeonsInitialized
  );

  const pendingEnrollmentsQuery = useInfiniteQuery({
    queryKey: [
      "get-pending-enrollments",
      selectedOrgId,
      queryString,
      surgeonFilter,
    ],
    queryFn: ({ pageParam }) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getPendingPatientEnrollments(
        selectedOrgId,
        queryString ? queryString : undefined,
        surgeonFilter.length !== orgSurgeons.length ||
          surgeonFilter.length === 0
          ? surgeonFilter
          : undefined,
        pageParam
      );
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
    enabled: !!selectedOrgId && isSurgeonsInitialized,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return pendingEnrollmentsQuery;
};

export const useGetPendingEnrollmentStatus = (
  pendingEnrollmentId?: string,
  enabled: boolean = false,
  timeoutMs: number = 120000 // 2 min timeout
) => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsTimedOut(false);
    }
  }, [enabled]);

  // Set timeout when polling starts
  useEffect(() => {
    if (!enabled) return;

    const timeoutId = setTimeout(() => {
      setIsTimedOut(true);
    }, timeoutMs);

    return () => clearTimeout(timeoutId);
  }, [enabled, timeoutMs]);

  return useQuery({
    queryKey: ["get-pending-enrollment", selectedOrgId, pendingEnrollmentId],
    queryFn: async () => {
      if (!selectedOrgId || !pendingEnrollmentId) {
        throw new Error("Missing pendingEnrollmentId or orgId");
      }
      const response = await getPendingPatientEnrollmentById(
        selectedOrgId,
        pendingEnrollmentId
      );
      return response;
    },
    enabled: enabled && !!selectedOrgId && !!pendingEnrollmentId,
    refetchInterval: (query) => {
      // check if pending enrollment data exists
      const hasValidData =
        query.state.data !== null && query.state.data !== undefined;
      const hasError = !!query.state.error;

      // stop polling if we have pending enrollment error (return false)
      if (hasValidData || hasError || isTimedOut) {
        return false; // Stop polling
      }
      return 3000; // poll every 3 sec
    },
    refetchIntervalInBackground: false,
    retry: false,
  });
};

// get pending enrollment by id
export const useGetPendingEnrollmenyById = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const pendingEnrollmentId = useBoundStore(
    (state) => state.pendingEnrollmentId
  );

  const pendingEnrollmentQuery = useQuery({
    queryKey: ["get-pending-enrollment", selectedOrgId, pendingEnrollmentId],
    queryFn: () => {
      if (!selectedOrgId || !pendingEnrollmentId) {
        return Promise.reject(
          new Error("Missing orgId or pending enrollment id")
        );
      }
      return getPendingPatientEnrollmentById(
        selectedOrgId,
        pendingEnrollmentId
      );
    },
    enabled: !!selectedOrgId && !!pendingEnrollmentId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return pendingEnrollmentQuery;
};

// create pending enrollment
export const useCreatePendingEnrollment = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

  const createPendingEnrollmentMutation = useMutation({
    mutationFn: async (pendingPatientEnrollment: PendingPatientEnrollment) => {
      if (!selectedOrgId) {
        throw new Error("Organization ID is missing.");
      }

      return await createPendingPatientEnrollment(
        selectedOrgId,
        pendingPatientEnrollment
      );
    },
    onError: (error, variables) => {
      console.error("Create Pending Patient Enrollment Error: ", {
        error,
        variables,
      });
    },
  });

  return createPendingEnrollmentMutation;
};

// update pending enrollment
export const useUpdatePendingEnrollment = () => {
  const queryClient = useQueryClient();

  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const pendingEnrollmentId = useBoundStore(
    (state) => state.pendingEnrollmentId
  );

  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);

  const updatePendingEnrollmentMutation = useMutation({
    mutationFn: async (pendingPatientEnrollment: PendingPatientEnrollment) => {
      if (!selectedOrgId || !pendingEnrollmentId) {
        throw new Error("Organization ID or pending enrollment id is missing.");
      }

      return await updatePendingPatientEnrollment(
        selectedOrgId,
        pendingEnrollmentId,
        pendingPatientEnrollment
      );
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          "get-pending-enrollments",
          selectedOrgId,
          queryString,
          surgeonFilter,
        ],
      });

      // Get the refreshed data with matching key
      const currentData = queryClient.getQueryData<
        InfiniteData<PendingEnrollmentPage>
      >(["get-pending-enrollments", selectedOrgId, queryString, surgeonFilter]);

      // Check if item exists in the refreshed data
      const existsInData = currentData?.pages.some((page) => {
        return page.data.some((item) => item.id === pendingEnrollmentId);
      });

      // If not found in refetched data, add it manually
      if (!existsInData) {
        queryClient.setQueryData<InfiniteData<PendingEnrollmentPage>>(
          [
            "get-pending-enrollments",
            selectedOrgId,
            queryString,
            surgeonFilter,
          ],
          (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page, index) => {
                // add at top of page
                if (index === 0) {
                  return {
                    ...page,
                    data: [
                      { id: pendingEnrollmentId, ...variables },
                      ...page.data,
                    ],
                  };
                }
                return page;
              }),
            };
          }
        );
      } else {
        queryClient.setQueryData<InfiniteData<PendingEnrollmentPage>>(
          [
            "get-pending-enrollments",
            selectedOrgId,
            queryString,
            surgeonFilter,
          ],
          (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.map((item) =>
                  item.id === pendingEnrollmentId
                    ? {
                        ...item,
                        ...variables,
                      }
                    : item
                ),
              })),
            };
          }
        );
      }
    },
    onError: (error, variables) => {
      console.error("Update Pending Patient Enrollment Error: ", {
        error,
        variables,
      });
    },
  });

  return updatePendingEnrollmentMutation;
};

// delete pending enrollment
export const useDeletePendingEnrollment = () => {
  const queryClient = useQueryClient();

  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);
  const pendingEnrollmentId = useBoundStore(
    (state) => state.pendingEnrollmentId
  );

  const deletePendingPatientEnrollmentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrgId || !pendingEnrollmentId) {
        throw new Error("Organization ID or pending enrollment id is missing.");
      }

      return await deletePendingPatientEnrollment(
        selectedOrgId,
        pendingEnrollmentId
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<InfiniteData<PendingEnrollmentPage>>(
        ["get-pending-enrollments", selectedOrgId, queryString, surgeonFilter],
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.filter(
                (enrollment: PendingPatientEnrollment) =>
                  enrollment.id !== pendingEnrollmentId
              ),
            })),
          };
        }
      );
    },
    onError: (error, variables) => {
      console.error("Update Pending Patient Enrollment Error: ", {
        error,
        variables,
      });
    },
  });

  return deletePendingPatientEnrollmentMutation;
};

// get patient enrollments
export const useGetPostOpEnrollments = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);
  const orgSurgeons = useBoundStore((state) => state.orgSurgeons);
  const isSurgeonsInitialized = useBoundStore(
    (state) => state.isSurgeonsInitialized
  );

  const enrollmentsPostOpQuery = useInfiniteQuery({
    queryKey: [
      "get-patient-enrollments-postop",
      selectedOrgId,
      queryString,
      surgeonFilter,
    ],
    queryFn: ({ pageParam }) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getPatientEnrollments(
        selectedOrgId,
        pageParam,
        queryString ? queryString : undefined,
        surgeonFilter.length !== orgSurgeons.length ||
          surgeonFilter.length === 0
          ? surgeonFilter
          : undefined
      );
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
    enabled: !!selectedOrgId && isSurgeonsInitialized,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return enrollmentsPostOpQuery;
};

export const useGetPreOpEnrollments = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);
  const orgSurgeons = useBoundStore((state) => state.orgSurgeons);
  const isSurgeonsInitialized = useBoundStore(
    (state) => state.isSurgeonsInitialized
  );

  const enrollmentsPreoOpQuery = useInfiniteQuery({
    queryKey: [
      "get-patient-enrollments-preop",
      selectedOrgId,
      queryString,
      surgeonFilter,
    ],
    queryFn: ({ pageParam }) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("Missing orgId"));
      }
      return getPatientEnrollments(
        selectedOrgId,
        pageParam,
        queryString ? queryString : undefined,
        surgeonFilter.length !== orgSurgeons.length ||
          surgeonFilter.length === 0
          ? surgeonFilter
          : undefined
      );
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
    enabled: !!selectedOrgId && isSurgeonsInitialized,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return enrollmentsPreoOpQuery;
};

// get patient enrollment by id
export const useGetEnrollmentById = (enrollmentId?: string) => {
  const enrollmentQuery = useQuery({
    queryKey: ["get-patient-enrollment", enrollmentId],
    queryFn: () => {
      if (!enrollmentId) {
        return Promise.reject(new Error("Missing enrollmentId"));
      }
      return getPatientEnrollmentById(enrollmentId);
    },
    enabled: !!enrollmentId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return enrollmentQuery;
};

export const useGetCareplanJson = (s3_url?: string) => {
  const setPatientCareplan = useBoundStore((state) => state.setPatientCareplan);
  const setCurrentCarePlan = useBoundStore((state) => state.setCurrentCarePlan);

  const enrollmentS3DataQuery = useQuery({
    queryKey: ["get-patient-enrollment-s3-data", s3_url],
    queryFn: async () => {
      if (!s3_url) {
        throw new Error("No S3 URL available");
      }
      return await fetchJsonData(s3_url);
    },
    enabled: !!s3_url,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (enrollmentS3DataQuery.data) {
      // patient dashboard
      setPatientCareplan(enrollmentS3DataQuery.data);
      // vpp dashboard
      setCurrentCarePlan(enrollmentS3DataQuery.data);
    }
  }, [enrollmentS3DataQuery.data, setPatientCareplan]);

  /***** local testing only *****/
  // useEffect(() => {
  //   if (s3_url) {
  //     setPatientCareplan(carePlanTemplate);
  //     setCurrentCarePlan(carePlanTemplate);
  //   }
  // }, [s3_url, setPatientCareplan]);
  /***** local testing only *****/

  return enrollmentS3DataQuery;
};

//create patient enrollment
export const useCreatePatientEnrollment = () => {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

  const createEnrollmentMutation = useMutation({
    mutationFn: async (
      createPatientEnrollmentRequest: CreatePatientEnrollmentRequest
    ) => {
      if (!selectedOrgId) {
        throw new Error("Organization ID is missing.");
      }

      return await createPatientEnrollment(
        selectedOrgId,
        createPatientEnrollmentRequest
      );
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error("Create Patient Enrollment Error: ", { error, variables });
    },
  });

  return createEnrollmentMutation;
};

// update patient enrollment
// export const useUpdatePatientEnrollment = () => {
//   const queryClient = useQueryClient();
//
//   const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

//   const updatePatientEnrollmentMutation = useMutation({
//     mutationFn: async ({
//       enrollmentId,
//       updatePatientEnrollmentRequest,
//     }: {
//       enrollmentId: string;
//       updatePatientEnrollmentRequest: UpdatePatientEnrollmentRequest;
//     }) => {
//       if (!orgId || !enrollmentId) {
//         throw new Error("Organization ID or enrollment id is missing.");
//       }

//       return await updatePatientEnrollment(
//         orgId,
//         enrollmentId,
//         updatePatientEnrollmentRequest
//       );
//     },
//     onSuccess: (data, variables) => {
//       // refetch care plan
//       queryClient.invalidateQueries({
//         queryKey: ["get-patient-enrollment", variables.enrollmentId],
//       });
//       queryClient.invalidateQueries({
//         queryKey: ["get-patient-enrollment-s3-data"],
//       });

//       // refetch action items
//       queryClient.invalidateQueries({
//         queryKey: ["get-enrollment-action-items"],
//       });
//     },
//     onError: (error, variables) => {
//       console.error("Update Patient Enrollment Error: ", {
//         error,
//         variables,
//       });
//     },
//   });

//   return updatePatientEnrollmentMutation;
// };

// delete patient enrollment
export const useDeleteEnrollment = () => {
  const queryClient = useQueryClient();

  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);
  const patientEnrollment = useBoundStore((state) => state.patientEnrollment);

  const deleteEnrollmentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrgId || !patientEnrollment?.enrollment.enrollment_id) {
        throw new Error("Organization ID or enrollment id is missing.");
      }

      return await deletePatientEnrollment(
        selectedOrgId,
        patientEnrollment.enrollment.enrollment_id
      );
    },
    onSuccess: () => {
      // delete from cache data
      queryClient.setQueryData<InfiniteData<EnrollmentPage>>(
        [
          "get-patient-enrollments-postop",
          selectedOrgId,
          queryString,
          surgeonFilter,
        ],
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.filter(
                (enrollment: PatientEnrollment) =>
                  enrollment.enrollment.enrollment_id !==
                  patientEnrollment?.enrollment.enrollment_id
              ),
            })),
          };
        }
      );

      queryClient.setQueryData<InfiniteData<EnrollmentPage>>(
        [
          "get-patient-enrollments-preop",
          selectedOrgId,
          queryString,
          surgeonFilter,
        ],
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.filter(
                (enrollment: PatientEnrollment) =>
                  enrollment.enrollment.enrollment_id !==
                  patientEnrollment?.enrollment.enrollment_id
              ),
            })),
          };
        }
      );
    },
    onError: (error, variables) => {
      console.error("Delete Patient Enrollment Error: ", {
        error,
        variables,
      });
    },
  });

  return deleteEnrollmentMutation;
};

// bulk delete patient enrollment items
export const useBulkDeleteEnrollmentItems = () => {
  const queryClient = useQueryClient();

  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);

  const deleteEnrollmentItemsMutation = useMutation({
    mutationFn: async ({
      enrollmentId,
      bulkDeleteItemsRequest,
    }: {
      enrollmentId: string;
      bulkDeleteItemsRequest: BulkDeleteItemsRequest;
    }) => {
      if (!selectedOrgId || !enrollmentId) {
        throw new Error("Organization ID or enrollment id is missing.");
      }

      return await deletePatientEnrollmentItems(
        selectedOrgId,
        enrollmentId,
        bulkDeleteItemsRequest
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["get-patient-enrollment", variables.enrollmentId],
      });
    },
    onError: (error, variables) => {
      console.error("Delete Patient Enrollment Items Error: ", {
        error,
        variables,
      });
    },
  });

  return deleteEnrollmentItemsMutation;
};

// poll getEnrollmentById for enrollment status (to "check" processing status)
export const useGetEnrollmentStatus = (
  enrollmentId?: string,
  timeoutMs: number = 120000 // 2 min timeout
) => {
  const queryClient = useQueryClient();

  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const queryString = useBoundStore((state) => state.queryString);
  const surgeonFilter = useBoundStore((state) => state.surgeonFilter);

  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    if (!enrollmentId) {
      setIsTimedOut(false);
    }
  }, [enrollmentId]);

  // Set timeout when polling starts
  useEffect(() => {
    if (!enrollmentId) return;

    const timeoutId = setTimeout(() => {
      setIsTimedOut(true);
    }, timeoutMs);

    return () => clearTimeout(timeoutId);
  }, [enrollmentId, timeoutMs]);

  const enrollmentStatusQuery = useQuery({
    queryKey: ["get-enrollment-status", enrollmentId],
    queryFn: async () => {
      if (!enrollmentId) {
        throw new Error("Missing enrollmentId");
      }
      return await getPatientEnrollmentById(enrollmentId);
    },
    enabled: !!enrollmentId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const hasError = !!query.state.error;

      // Stop polling if ACTIVE, error, or timeout
      if (status === "ACTIVE" || hasError || isTimedOut) {
        return false;
      }
      return 3000; // poll every 3 seconds
    },
    refetchIntervalInBackground: false,
    retry: false,
  });

  // Refetch enrollments list when status becomes ACTIVE or times out
  useEffect(() => {
    const refetchEnrollments = async () => {
      if (enrollmentStatusQuery.data?.status === "ACTIVE") {
        if (
          !enrollmentStatusQuery.data.patient_id ||
          !enrollmentStatusQuery.data.organization_id
        ) {
          throw new Error("Patient ID or Org ID is missing");
        }
        const patient = await getPatientById(
          enrollmentStatusQuery.data.patient_id
        );

        const newEnrollment = {
          org_id: enrollmentStatusQuery.data.organization_id,
          patient: patient,
          enrollment: enrollmentStatusQuery.data,
        };

        await queryClient.invalidateQueries({
          queryKey: [
            "get-patient-enrollments-preop",
            selectedOrgId,
            queryString,
            surgeonFilter,
          ],
        });

        // Get the refreshed data with matching key
        const currentData = queryClient.getQueryData<
          InfiniteData<EnrollmentPage>
        >([
          "get-patient-enrollments-preop",
          selectedOrgId,
          queryString,
          surgeonFilter,
        ]);

        // Check if item exists in the refreshed data
        const existsInDataAndActive = currentData?.pages.some((page) =>
          page.data.some(
            (item) =>
              item.enrollment.enrollment_id ===
                enrollmentStatusQuery.data?.enrollment_id &&
              item.enrollment.status === "ACTIVE"
          )
        );

        if (!existsInDataAndActive) {
          queryClient.setQueryData<InfiniteData<EnrollmentPage>>(
            [
              "get-patient-enrollments-preop",
              selectedOrgId,
              queryString,
              surgeonFilter,
            ],
            (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page, index) => {
                  // Add to the last page at the end
                  if (index === oldData.pages.length - 1) {
                    return {
                      ...page,
                      data: [...page.data, newEnrollment],
                    };
                  }
                  return page;
                }),
              };
            }
          );
        } else {
          queryClient.setQueryData<InfiniteData<EnrollmentPage>>(
            [
              "get-patient-enrollments-preop",
              selectedOrgId,
              queryString,
              surgeonFilter,
            ],
            currentData
          );
        }
      }
    };

    refetchEnrollments();
  }, [enrollmentStatusQuery.data?.status, queryClient]);

  return {
    enrollmentStatusQuery,
    isTimedOut,
  };
};
